import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../../config/firebase";
import {
  doc,
  collection,
  addDoc,
  setDoc,
  deleteDoc,
  getDocs,
} from "firebase/firestore";
import { useSubscription } from "../../../../hooks/useSubscription";

type Props = {
  className: string;
  userID: string;
};

interface Opportunity {
  id: string;
  title: string;
  description: string;
  category: string;
  source: "trending" | "ai-suggested";
  marketSize?: string;
  growthRate?: string;
  difficulty: "low" | "medium" | "high";
}

const categoryConfig: Record<string, { label: string; icon: string }> = {
  ai: { label: "AI/ML", icon: "bi-robot" },
  sustainability: { label: "Sustainability", icon: "bi-leaf" },
  health: { label: "Health", icon: "bi-heart-pulse" },
  fintech: { label: "FinTech", icon: "bi-bank" },
  saas: { label: "SaaS", icon: "bi-cloud" },
  marketplace: { label: "Marketplace", icon: "bi-shop" },
  education: { label: "Education", icon: "bi-mortarboard" },
  ecommerce: { label: "E-Commerce", icon: "bi-cart" },
};

const difficultyConfig: Record<string, { label: string; color: string }> = {
  low: { label: "Entry Level", color: "success" },
  medium: { label: "Moderate", color: "warning" },
  high: { label: "Advanced", color: "danger" },
};

const OPPORTUNITIES: Opportunity[] = [
  {
    id: "opp-1",
    title: "AI-Powered Resume Optimizer",
    description:
      "Help job seekers optimize their resumes using AI to match job descriptions, highlight key skills, and increase interview callback rates by up to 40%.",
    category: "ai",
    source: "ai-suggested",
    marketSize: "$2.5B",
    growthRate: "18%",
    difficulty: "medium",
  },
  {
    id: "opp-2",
    title: "Carbon Footprint Tracker for SMBs",
    description:
      "Simple tool for small businesses to track, report, and reduce their carbon emissions with actionable insights and regulatory compliance reporting.",
    category: "sustainability",
    source: "trending",
    marketSize: "$1.8B",
    growthRate: "25%",
    difficulty: "medium",
  },
  {
    id: "opp-3",
    title: "Mental Health Check-in App",
    description:
      "Daily mental wellness tracker with mood journaling, AI-powered pattern insights, and on-demand connection to licensed therapists.",
    category: "health",
    source: "ai-suggested",
    marketSize: "$5.2B",
    growthRate: "22%",
    difficulty: "high",
  },
  {
    id: "opp-4",
    title: "Micro-Investment Platform for Teens",
    description:
      "Educational investing app that teaches financial literacy through guided lessons while allowing small investments with parental oversight and controls.",
    category: "fintech",
    source: "trending",
    marketSize: "$800M",
    growthRate: "30%",
    difficulty: "high",
  },
  {
    id: "opp-5",
    title: "Local Service Marketplace",
    description:
      "Connect local service providers like plumbers, electricians, and cleaners with homeowners, featuring verified reviews, instant booking, and secure payments.",
    category: "marketplace",
    source: "ai-suggested",
    marketSize: "$12B",
    growthRate: "15%",
    difficulty: "low",
  },
  {
    id: "opp-6",
    title: "AI Meeting Summarizer for Remote Teams",
    description:
      "Automatically record, transcribe, and summarize virtual meetings with action items, decisions made, and follow-up assignments sent to each participant.",
    category: "ai",
    source: "trending",
    marketSize: "$3.1B",
    growthRate: "28%",
    difficulty: "medium",
  },
  {
    id: "opp-7",
    title: "Sustainable Packaging Marketplace",
    description:
      "B2B platform connecting businesses with eco-friendly packaging suppliers, offering cost comparisons, carbon savings calculations, and bulk ordering.",
    category: "sustainability",
    source: "ai-suggested",
    marketSize: "$4.7B",
    growthRate: "20%",
    difficulty: "medium",
  },
  {
    id: "opp-8",
    title: "Online Tutoring Platform for STEM",
    description:
      "Connect students with vetted STEM tutors for 1-on-1 live sessions, featuring interactive whiteboards, progress tracking, and curriculum-aligned content.",
    category: "education",
    source: "trending",
    marketSize: "$7.8B",
    growthRate: "16%",
    difficulty: "medium",
  },
  {
    id: "opp-9",
    title: "Subscription Box Curation Engine",
    description:
      "White-label platform for entrepreneurs to launch curated subscription boxes with supplier sourcing, inventory management, and customer analytics built in.",
    category: "ecommerce",
    source: "ai-suggested",
    marketSize: "$2.2B",
    growthRate: "19%",
    difficulty: "low",
  },
  {
    id: "opp-10",
    title: "AI Personal Finance Coach",
    description:
      "Conversational AI that analyzes spending habits, provides personalized budgeting advice, automates savings goals, and alerts users to unusual charges.",
    category: "fintech",
    source: "ai-suggested",
    marketSize: "$1.5B",
    growthRate: "24%",
    difficulty: "medium",
  },
  {
    id: "opp-11",
    title: "Remote Team Culture Platform",
    description:
      "SaaS tool for distributed teams to build culture through virtual events, peer recognition, wellness challenges, and pulse surveys with analytics dashboards.",
    category: "saas",
    source: "trending",
    marketSize: "$900M",
    growthRate: "32%",
    difficulty: "medium",
  },
  {
    id: "opp-12",
    title: "Pet Health & Wellness Tracker",
    description:
      "Mobile app for pet owners to log vet visits, track medications, monitor diet and exercise, and receive breed-specific health alerts and preventive care tips.",
    category: "health",
    source: "trending",
    marketSize: "$1.2B",
    growthRate: "21%",
    difficulty: "low",
  },
  {
    id: "opp-13",
    title: "AI-Powered Code Review Tool",
    description:
      "Developer tool that automatically reviews pull requests for bugs, security vulnerabilities, performance issues, and coding standard compliance.",
    category: "ai",
    source: "ai-suggested",
    marketSize: "$2.8B",
    growthRate: "35%",
    difficulty: "high",
  },
  {
    id: "opp-14",
    title: "Niche Freelancer Marketplace",
    description:
      "Specialized marketplace connecting businesses with pre-vetted freelancers in niche fields like 3D modelling, grant writing, or patent illustration.",
    category: "marketplace",
    source: "ai-suggested",
    marketSize: "$6.3B",
    growthRate: "17%",
    difficulty: "medium",
  },
  {
    id: "opp-15",
    title: "Small Business Compliance Automation",
    description:
      "Automate regulatory compliance for small businesses including GDPR, tax filings, employment law, and industry-specific requirements with guided workflows.",
    category: "saas",
    source: "trending",
    marketSize: "$3.5B",
    growthRate: "23%",
    difficulty: "high",
  },
  {
    id: "opp-16",
    title: "Digital Menu & Ordering for Restaurants",
    description:
      "QR-code-based digital menus with built-in ordering, kitchen display integration, loyalty programs, and customer feedback collection for independent restaurants.",
    category: "ecommerce",
    source: "trending",
    marketSize: "$1.9B",
    growthRate: "26%",
    difficulty: "low",
  },
];

type FilterKey = "all" | "ai-suggested" | "trending" | "saved";

const OpportunitiesWidget: React.FC<Props> = ({ className, userID }) => {
  const navigate = useNavigate();
  const { canUseFeature } = useSubscription();
  const [filter, setFilter] = useState<FilterKey>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [validatingId, setValidatingId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  // Load saved opportunities from Firestore
  useEffect(() => {
    if (!userID) return;
    const loadSaved = async () => {
      try {
        const savedRef = collection(doc(db, "users", userID), "savedOpportunities");
        const snapshot = await getDocs(savedRef);
        const ids = new Set(snapshot.docs.map((d) => d.id));
        setSavedIds(ids);
      } catch (err) {
        console.error("Failed to load saved opportunities:", err);
      }
    };
    loadSaved();
  }, [userID]);

  // Load dismissed from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("dismissedOpportunities");
    if (stored) {
      try {
        setDismissedIds(new Set(JSON.parse(stored)));
      } catch {
        // ignore
      }
    }
  }, []);

  const filteredOpportunities = OPPORTUNITIES.filter((opp) => {
    if (dismissedIds.has(opp.id)) return false;
    if (filter === "saved") return savedIds.has(opp.id);
    if (filter === "all") return true;
    return opp.source === filter;
  });

  const handleSave = useCallback(
    async (opportunity: Opportunity) => {
      if (!userID || savingId) return;
      setSavingId(opportunity.id);
      try {
        const savedDocRef = doc(db, "users", userID, "savedOpportunities", opportunity.id);
        const isSaved = savedIds.has(opportunity.id);
        if (isSaved) {
          await deleteDoc(savedDocRef);
          setSavedIds((prev) => {
            const next = new Set(prev);
            next.delete(opportunity.id);
            return next;
          });
        } else {
          await setDoc(savedDocRef, {
            title: opportunity.title,
            category: opportunity.category,
            savedAt: new Date().toISOString(),
          });
          setSavedIds((prev) => new Set(prev).add(opportunity.id));
        }
      } catch (err) {
        console.error("Failed to save opportunity:", err);
      } finally {
        setSavingId(null);
      }
    },
    [userID, savedIds, savingId]
  );

  const handleDismiss = useCallback(
    (opportunityId: string) => {
      setDismissedIds((prev) => {
        const next = new Set(prev).add(opportunityId);
        localStorage.setItem("dismissedOpportunities", JSON.stringify([...next]));
        return next;
      });
      if (expandedId === opportunityId) setExpandedId(null);
    },
    [expandedId]
  );

  const handleValidate = useCallback(
    async (opportunity: Opportunity) => {
      if (!userID || validatingId) return;
      setValidatingId(opportunity.id);
      try {
        const useAI =
          canUseFeature("ai_autofill") && opportunity.description.length >= 50;
        const userRef = doc(db, "users", userID);
        const projectsRef = collection(userRef, "projects");
        const projectDoc = await addDoc(projectsRef, {
          projectTitle: opportunity.title,
          projectDescription: opportunity.description,
          projectProgress: 0,
          aiGenerated: useAI,
          createdAt: new Date().toISOString(),
        });
        const projectID = projectDoc.id;
        if (useAI) {
          navigate("/ai-progress", {
            state: {
              userID,
              projectID,
              projectName: opportunity.title,
              projectDescription: opportunity.description,
            },
          });
        } else {
          navigate("/project", {
            state: {
              userID,
              projectID,
              projectTitle: opportunity.title,
              projectDescription: opportunity.description,
            },
          });
        }
      } catch (err) {
        console.error("Failed to create project from opportunity:", err);
        setValidatingId(null);
      }
    },
    [userID, validatingId, canUseFeature, navigate]
  );

  return (
    <div className={`card ${className}`}>
      {/* Header */}
      <div className="card-header border-0 pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bolder text-dark fs-3">
            Opportunities to Validate
          </span>
          <span className="text-muted mt-1 fw-bold fs-7">
            Curated business ideas ready for the 24-step framework
          </span>
        </h3>
      </div>

      {/* Body */}
      <div className="card-body pt-3">
        {/* Filters */}
        <div className="d-flex flex-wrap gap-2 mb-5">
          {(
            [
              { key: "all", label: "All", icon: "bi-grid" },
              { key: "ai-suggested", label: "AI Picks", icon: "bi-robot" },
              { key: "trending", label: "Trending", icon: "bi-graph-up-arrow" },
              { key: "saved", label: "Saved", icon: "bi-bookmark" },
            ] as const
          ).map((f) => (
            <button
              key={f.key}
              className={`btn btn-sm ${filter === f.key ? "btn-primary" : "btn-light"}`}
              onClick={() => setFilter(f.key)}
            >
              <i className={`bi ${f.icon} me-1`}></i>
              {f.label}
              {f.key === "saved" && savedIds.size > 0 && (
                <span className="badge badge-circle badge-white ms-1" style={{ fontSize: "9px" }}>
                  {savedIds.size}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Opportunities List */}
        <div className="d-flex flex-column gap-3">
          {filteredOpportunities.length === 0 ? (
            <div className="text-center py-10">
              <i className="bi bi-inbox text-gray-400 fs-2x d-block mb-3"></i>
              <span className="text-gray-500 fs-7">
                {filter === "saved"
                  ? "No saved opportunities yet. Bookmark ideas you like."
                  : "No opportunities match this filter."}
              </span>
            </div>
          ) : (
            filteredOpportunities.map((opportunity) => {
              const category = categoryConfig[opportunity.category] || {
                label: "Other",
                icon: "bi-box",
              };
              const difficulty = difficultyConfig[opportunity.difficulty] || {
                label: "Unknown",
                color: "secondary",
              };
              const isExpanded = expandedId === opportunity.id;
              const isSaved = savedIds.has(opportunity.id);
              const isValidating = validatingId === opportunity.id;
              const isSaving = savingId === opportunity.id;

              return (
                <div
                  key={opportunity.id}
                  className={`card card-bordered ${isExpanded ? "border-primary" : "border-gray-200"}`}
                  style={{ transition: "border-color 0.2s ease" }}
                >
                  <div className="card-body p-5">
                    {/* Header Row */}
                    <div className="d-flex align-items-start">
                      <div className="symbol symbol-40px me-4">
                        <div className="symbol-label bg-light">
                          <i className={`bi ${category.icon} text-gray-600 fs-4`}></i>
                        </div>
                      </div>

                      <div className="flex-grow-1 min-w-0">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <h5 className="fw-bolder text-gray-800 mb-0 text-truncate me-2">
                            {opportunity.title}
                          </h5>
                          <div className="d-flex gap-1 flex-shrink-0">
                            <button
                              className={`btn btn-sm btn-icon ${isSaved ? "btn-light-warning" : "btn-light"}`}
                              onClick={() => handleSave(opportunity)}
                              disabled={isSaving}
                              title={isSaved ? "Unsave" : "Save"}
                            >
                              <i className={`bi ${isSaved ? "bi-bookmark-fill text-warning" : "bi-bookmark text-gray-500"}`}></i>
                            </button>
                            <button
                              className="btn btn-sm btn-icon btn-light"
                              onClick={() => setExpandedId(isExpanded ? null : opportunity.id)}
                              title={isExpanded ? "Collapse" : "Expand"}
                            >
                              <i className={`bi bi-chevron-${isExpanded ? "up" : "down"} text-gray-500`}></i>
                            </button>
                          </div>
                        </div>

                        <div className="d-flex flex-wrap gap-1 mb-2">
                          <span className="badge badge-light fs-9">{category.label}</span>
                          <span className={`badge badge-light-${difficulty.color} fs-9`}>
                            {difficulty.label}
                          </span>
                          {opportunity.source === "ai-suggested" && (
                            <span className="badge badge-light fs-9">
                              <i className="bi bi-robot me-1"></i>AI Pick
                            </span>
                          )}
                          {opportunity.source === "trending" && (
                            <span className="badge badge-light fs-9">
                              <i className="bi bi-graph-up-arrow me-1"></i>Trending
                            </span>
                          )}
                        </div>

                        <p className="text-gray-600 fs-7 mb-0" style={{ lineHeight: "1.5" }}>
                          {opportunity.description}
                        </p>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-top border-gray-200">
                        <div className="row g-3 mb-4">
                          {opportunity.marketSize && (
                            <div className="col-6 col-md-3">
                              <div className="bg-light rounded p-3 text-center">
                                <div className="fs-8 text-gray-500 mb-1">Market Size</div>
                                <div className="fw-bolder text-gray-800">{opportunity.marketSize}</div>
                              </div>
                            </div>
                          )}
                          {opportunity.growthRate && (
                            <div className="col-6 col-md-3">
                              <div className="bg-light rounded p-3 text-center">
                                <div className="fs-8 text-gray-500 mb-1">Growth Rate</div>
                                <div className="fw-bolder text-gray-800">{opportunity.growthRate}</div>
                              </div>
                            </div>
                          )}
                          <div className="col-6 col-md-3">
                            <div className="bg-light rounded p-3 text-center">
                              <div className="fs-8 text-gray-500 mb-1">Difficulty</div>
                              <div className={`fw-bolder text-${difficulty.color}`}>
                                {difficulty.label}
                              </div>
                            </div>
                          </div>
                          <div className="col-6 col-md-3">
                            <div className="bg-light rounded p-3 text-center">
                              <div className="fs-8 text-gray-500 mb-1">Category</div>
                              <div className="fw-bolder text-gray-800">{category.label}</div>
                            </div>
                          </div>
                        </div>

                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-primary btn-sm flex-grow-1"
                            onClick={() => handleValidate(opportunity)}
                            disabled={isValidating}
                          >
                            {isValidating ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Creating...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-play-circle me-1"></i>
                                Start Validation
                              </>
                            )}
                          </button>
                          <button
                            className="btn btn-light btn-sm"
                            onClick={() => handleDismiss(opportunity.id)}
                            title="Not interested"
                          >
                            <i className="bi bi-x-lg text-gray-500"></i>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export { OpportunitiesWidget };
